import { Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { FormService } from '../../../services/form/form.service';
import { ParserService } from '../../../../../common/angular-shared/services/parser/parser.service';
import { BasicTextInputComponent } from '../base-components/base-text-input/basic-text-input.component';

/**
 * Defines special types and collections for the DynamicValidation component
 */
// tslint:disable-next-line
namespace DynamicValidation {
  /**
   * Designates a dynamic validation field requirement
   */
  export type Requirement = {
    /**
     * A descriptive name for the validation requirement
     */
    name: string;
    /**
     * The regular expression to be checked against the input value
     */
    regExp: RegExp;
    /**
     * A verbal description of the requirement, to be rendered on the page
     */
    description: string;
  };

  export const lowercaseLetterRequirement: Requirement = {
    name: 'hasLowercaseLetter',
    regExp: /[a-z]/,
    description: 'Lowercase letter',
  };

  export const uppercaseLetterRequirement: Requirement = {
    name: 'hasUppercaseLetter',
    regExp: /[A-Z]/,
    description: 'Uppercase letter',
  };

  export const numberRequirement: Requirement = {
    name: 'hasNumber',
    regExp: /\d/,
    description: 'Number',
  };

  export const specialCharacterRequirement: Requirement = {
    name: 'hasSpecialCharacter',
    regExp: /[?!%$#&+\-_*@]/,
    description: 'Special character: ? ! % $ # & + - _ * @',
  };

  export const eightToThirtyCharactersRequirement: Requirement = {
    name: 'hasEightToThirtyCharacters',
    regExp: /^\S{8,30}$/,
    description: '8-30 characters',
  };

  /**
   *  An extensible object that holds key/value pairs comprising possible
   *  validation profiles (e.g. 'default', 'alphaOnly, 'noSpecialChars',
   *  etc.) represented as arrays of `Requirement`s
   */
  export const requirementProfiles: { default: Requirement[] } = {
    default: [
      lowercaseLetterRequirement,
      uppercaseLetterRequirement,
      numberRequirement,
      specialCharacterRequirement,
      eightToThirtyCharactersRequirement,
    ],
  };
}

@Component({
  moduleId: module.id,
  selector: 'dynamic-validation',
  templateUrl: './dynamic-validation.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicValidationComponent),
      multi: true,
    },
  ],
})
export class DynamicValidationComponent extends BasicTextInputComponent {
  @Input() requirementProfile: string = 'default';
  requirements: DynamicValidation.Requirement[];
  validationResults: boolean[];
  showRequirements: boolean = false;
  inputIsVisible: boolean = false;

  constructor(protected formService: FormService, protected parserService: ParserService) {
    super(formService, parserService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.updateFromModel();
    this.validateInput();
  }

  getRequirements(profile: string): DynamicValidation.Requirement[] {
    profile = profile || 'default';
    return DynamicValidation.requirementProfiles[profile];
  }

  validateInput(): void {
    const inputValue: string = this.val;
    // Get list of validation requirements based on given profile
    this.requirements = this.getRequirements(this.requirementProfile);
    // Get corresponding list of validation results (boolean values)
    this.validationResults = this.requirements.map((requirement) => {
      return requirement.regExp.test(inputValue);
    });
    // Run base form validation if all requirements are met
    if (this.checkRequirementsMet()) {
      this.trigger();
    }
  }

  displayRequirements(): void {
    this.showRequirements = true;
  }

  hideRequirements(): void {
    if (this.fieldIsEmpty()) {
      this.showRequirements = false;
    }
  }

  fieldIsEmpty(): boolean {
    return !this.val;
  }

  toggleInputVisibility(): void {
    const inputElement: HTMLInputElement = <HTMLInputElement>document.getElementById(this.componentId);
    if (inputElement.type === 'password') {
      this.inputIsVisible = true;
      inputElement.type = 'text';
    } else {
      this.inputIsVisible = false;
      inputElement.type = 'password';
    }
  }

  checkRequirementsMet(): boolean {
    // Check that all requirements have been fulfilled
    return this.validationResults.every((result) => result);
  }
}
